import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '@assets/css/common/style_main.css';
import '@assets/css/common/style_table.css';
import '@assets/css/common/style_modal.css';
import '@assets/css/common/style_calendar.css';
import Calendar from '@components/common/Calendar.jsx';
import PaginationZeroBased from '@components/common/PaginationZeroBased.jsx';

import CameraAngleInputModal from './components/CameraAngleInputModal.jsx'
import CameraAngleDetailModal from './components/CameraAngleDetailModal.jsx'
import CameraAngleConfirmModal from './components/CameraAngleConfirmModal.jsx'
import cameraAngleApi from '@services/cameraAngleApi.jsx';
import cameraAngleFileApi from '@services/cameraAngleFileApi.jsx';
import { useAuthStore } from '@/store/useAuthStore.jsx';

const CameraAngleManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // 상태 관리
  const [cameraAngles, setCameraAngles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10,
    hasNext: false,
    hasPrevious: false
  });
  
  // 검색 및 필터 상태
  const [searchFilters, setSearchFilters] = useState({
    angleName: '',
    angleType: '',
    isDeployed: null,
    sortBy: 'createdAt',
    sortDirection: 'desc'
  });
  
  // 체크박스 상태 관리
  const [selectAll, setSelectAll] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // 모달 상태 관리
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // 모달 메시지 상태
  const [alertMessage, setAlertMessage] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [currentAngle, setCurrentAngle] = useState(null);
  
  // 모달 입력값 상태 관리
  const [modalInputs, setModalInputs] = useState({
    author: '',
    angleName: '',
    startDatetime: '',
    endDatetime: '',
    isDeployed: false,
    cameraData: '',
    angleType: '',
    angleDataFile: null,
    angleDataResourceId: null,
    angleDirection: null,
  });
  
  // 지점 관련 상태
  const [availableBranches, setAvailableBranches] = useState([
    { id: 'branch-1', name: '신사점' }
  ]);
  
  const [selectedBranches, setSelectedBranches] = useState([
    { id: 'branch-default', name: '강남점' }
  ]);
  
  const [selectedAvailableBranch, setSelectedAvailableBranch] = useState(null);
  const [selectedAddedBranch, setSelectedAddedBranch] = useState(null);

  // 카메라 앵글 목록 조회
  const fetchCameraAngles = useCallback(async (page = 0) => {
    setLoading(true);
    try {
      const params = {
        page,
        size: pagination.size,
        sortBy: searchFilters.sortBy,
        sortDirection: searchFilters.sortDirection,
        ...searchFilters
      };

      // 빈 값 필터링
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await cameraAngleApi.getCameraAngles(params);
      
      if (response.success) {
        // BaseResponse<Page<CameraAngleResponse>> 구조 처리
        const pageData = response.data;
        setCameraAngles(pageData.content || []);
        setPagination({
          currentPage: pageData.number || 0,
          totalPages: pageData.totalPages || 0,
          totalElements: pageData.totalElements || 0,
          size: pageData.size || 10,
          hasNext: pageData.hasNext || false,
          hasPrevious: pageData.hasPrevious || false
        });
      }
    } catch (error) {
      console.error('카메라 앵글 목록 조회 실패:', error);
      setAlertMessage('카메라 앵글 목록을 불러오는데 실패했습니다.');
      setShowAlertModal(true);
    } finally {
      setLoading(false);
    }
  }, [pagination.size, searchFilters]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchCameraAngles();
  }, []);

  // 필터 변경 시 첫 페이지로 이동하여 재조회
  useEffect(() => {
    if (pagination.currentPage !== 0) {
      fetchCameraAngles(0);
    } else {
      fetchCameraAngles();
    }
  }, [searchFilters]);

  // 체크박스 전체 선택/해제 핸들러
  const handleSelectAll = (e) => {
    setSelectAll(e.target.checked);
    setSelectedItems(e.target.checked ? cameraAngles.map(angle => angle.id) : []);
  };
  
  // 개별 체크박스 핸들러
  const handleItemSelect = (id, e) => {
    if (e.target.checked) {
      setSelectedItems([...selectedItems, id]);
    } else {
      setSelectedItems(selectedItems.filter(item => item !== id));
    }
    
    // 전체 선택 상태 업데이트
    if (e.target.checked && selectedItems.length + 1 === cameraAngles.length) {
      setSelectAll(true);
    } else if (!e.target.checked && selectAll) {
      setSelectAll(false);
    }
  };

  // 필터 변경 핸들러
  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // 카메라 앵글 추가 버튼 핸들러
  const handleAddAngleClick = () => {
    setModalInputs({
      author: user?.name || 'system',
      angleName: '',
      startDatetime: '',
      endDatetime: '',
      isDeployed: false,
      cameraData: '',
      angleType: 'PHOTO',
      angleDataFile: null,
      angleDirection: null,
    });
    
    setAvailableBranches([
      { id: 'branch-1', name: '신사점' }
    ]);
    
    setSelectedBranches([
      { id: 'branch-default', name: '강남점' }
    ]);
    
    setSelectedAvailableBranch(null);
    setSelectedAddedBranch(null);
    
    setShowAddModal(true);
  };
  
  // 편집 버튼 핸들러
  const handleEditClick = async (angle) => {
    setCurrentAngle(angle);

    setModalInputs({
      id: angle.id, // ID 추가
      author: user?.name || 'system',
      angleName: angle.angleName,
      startDatetime: formatDateForInput(angle.startDatetime),
      endDatetime: formatDateForInput(angle.endDatetime),
      isDeployed: angle.isDeployed || false,
      cameraData: angle.originalFilename || '',
      angleType: angle.angleTypeCode || getAngleTypeCodeFromBoolean(angle.isPhoto, angle.isVideo, angle.isGreeting),
      angleDataFile: null,
      angleDirection: angle.angleDirection || 'LANDSCAPE',
    });
    
    // 지점 정보 설정
    const branches = angle.branches || [];
    setSelectedBranches([
      { id: 'branch-default', name: '강남점' },
      ...branches.map(b => ({ id: b.branchId, name: b.branchName }))
    ]);
    
    setAvailableBranches([
      { id: 'branch-1', name: '신사점' }
    ]);
    
    setSelectedAvailableBranch(null);
    setSelectedAddedBranch(null);
    
    setShowEditModal(true);
  };
  
  // 삭제 버튼 핸들러
  const handleDeleteClick = (angle) => {
    setCurrentAngle(angle);
    setConfirmMessage(`${angle.angleName} 카메라 앵글을 삭제하시겠습니까?`);
    setConfirmCallback(() => async () => {
      try {
        const response = await cameraAngleApi.deleteCameraAngle(angle.id);
        if (response.success) {
          setAlertMessage(response.message || '삭제되었습니다.');
          setShowAlertModal(true);
          fetchCameraAngles(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '삭제에 실패했습니다.');
          setShowAlertModal(true);
        }
      } catch (error) {
        console.error('삭제 실패:', error);
        const errorMessage = error.response?.data?.message || '삭제에 실패했습니다.';
        setAlertMessage(errorMessage);
        setShowAlertModal(true);
      }
    });
    setShowConfirmModal(true);
  };
  
  // 중복 확인 핸들러
  const handleCheckDuplicate = async () => {
    const angleName = modalInputs.angleName.trim();
    if (!angleName) {
      setAlertMessage('카메라 앵글명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    try {
      const excludeId = showEditModal && currentAngle ? currentAngle.id : null;
      const response = await cameraAngleApi.checkAngleNameDuplicate(angleName, excludeId);
      
      if (response.success) {
        setAlertMessage(response.data.message || '중복 확인 완료');
      } else {
        setAlertMessage(response.message || '중복 확인 실패');
      }
      setShowAlertModal(true);
    } catch (error) {
      console.error('중복 확인 실패:', error);
      setAlertMessage('중복 확인에 실패했습니다.');
      setShowAlertModal(true);
    }
  };
  
  // 카메라 앵글 추가/수정 확인 핸들러
  const handleConfirm = async (angleFiles = []) => {
    // 입력값 검증
    if (!modalInputs.angleName) {
      setAlertMessage('카메라 앵글명을 입력해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    if (!modalInputs.startDatetime || !modalInputs.endDatetime) {
      setAlertMessage('사용 기간을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }
    
    if (!modalInputs.angleType) {
      setAlertMessage('앵글 타입을 선택해주세요.');
      setShowAlertModal(true);
      return;
    }

    try {
      // 앵글 타입 코드를 boolean 값으로 변환
      const angleTypeValues = getBooleanFromAngleTypeCode(modalInputs.angleType);
      
      // FormData 생성
      const requestData = {
        angleName: modalInputs.angleName,
        isPhoto: angleTypeValues.isPhoto || false,
        isVideo: angleTypeValues.isVideo || false,
        isGreeting: angleTypeValues.isGreeting || false,
        startDatetime: modalInputs.startDatetime,
        endDatetime: modalInputs.endDatetime,
        isDeployed: modalInputs.isDeployed,
        createdBy: modalInputs.author || 'system',
        updatedBy: modalInputs.author || 'system',
        angleDirection: modalInputs.angleDirection || 'LANDSCAPE',
        // branches: selectedBranches
        //   .filter(b => b.id !== 'branch-default')
        //   .map(b => ({
        //     branchId: b.id,
        //     branchName: b.name,
        //     isActive: true
        //   }))
      };
      
      // 디버깅용 로그
      console.log('앵글 타입 코드:', modalInputs.angleType);
      console.log('변환된 boolean 값들:', angleTypeValues);
      console.log('전송할 데이터:', requestData);

      if (modalInputs.angleDataFile) {
        requestData.angleDataFile = modalInputs.angleDataFile;
      }

      const formData = cameraAngleApi.createFormData(requestData);

      if (showAddModal) {
        const response = await cameraAngleApi.createCameraAngle(formData);
        
        if (response.success) {
          // 앵글 생성 성공 시 임시 파일들을 실제로 등록
          if (angleFiles && angleFiles.length > 0) {
            try {
              for (let i = 0; i < angleFiles.length; i++) {
                const file = angleFiles[i];
                
                // 백엔드 API에 맞는 데이터 구조로 변환
                const fileData = {
                  fileName: file.fileName || file.name,
                  originalFileName: file.originalFileName || file.name,
                  fileResourceId: file.fileResourceId,
                  displayOrder: file.displayOrder || (i + 1),
                  isActive: file.isActive !== undefined ? file.isActive : true,
                };
                
                const fileResponse = await cameraAngleFileApi.addFile(response.data.id, fileData);
                
                if (!fileResponse.success) {
                  console.error('파일 등록 실패:', fileResponse.message);
                  throw new Error(fileResponse.message || '파일 등록 실패');
                }
              }
            } catch (fileError) {
              console.error('앵글 파일 등록 실패:', fileError);
              setAlertMessage(`앵글은 생성되었지만 파일 등록에 실패했습니다: ${fileError.message}`);
              setShowAlertModal(true);
            }
          }
          
          setShowAddModal(false);
          
          // 카메라 앵글 설정 확인
          setConfirmMessage('카메라 앵글 설정을 진행하시겠습니까?');
          setConfirmCallback(() => () => {
            navigate(`/camera-angle-management/angle-setting?angle=${response.data.angleName}&author=${modalInputs.author}&date=${modalInputs.startDatetime}`);
          });
          setShowConfirmModal(true);
          
          fetchCameraAngles(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '생성에 실패했습니다.');
          setShowAlertModal(true);
        }
      } else if (showEditModal && currentAngle) {
        const response = await cameraAngleApi.updateCameraAngle(currentAngle.id, formData);
        
        if (response.success) {
          setAlertMessage(response.message || '카메라 앵글 정보가 수정되었습니다.');
          setShowEditModal(false);
          setShowAlertModal(true);
          
          fetchCameraAngles(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '수정에 실패했습니다.');
          setShowAlertModal(true);
        }
      }
    } catch (error) {
      console.error('처리 실패:', error);
      setAlertMessage('처리 중 오류가 발생했습니다.');
      setShowAlertModal(true);
    }
  };

  // 일괄 삭제 핸들러
  const handleBulkDelete = () => {
    if (selectedItems.length === 0) return;
    
    setConfirmMessage(`선택한 ${selectedItems.length}개의 카메라 앵글을 삭제하시겠습니까?`);
    setConfirmCallback(() => async () => {
      try {
        const response = await cameraAngleApi.deleteCameraAngles(selectedItems);
        if (response.success) {
          setSelectedItems([]);
          setSelectAll(false);
          setAlertMessage(response.message || response.data || '선택한 카메라 앵글이 삭제되었습니다.');
          setShowAlertModal(true);
          fetchCameraAngles(pagination.currentPage);
        } else {
          setAlertMessage(response.message || '삭제에 실패했습니다.');
          setShowAlertModal(true);
        }
      } catch (error) {
        console.error('일괄 삭제 실패:', error);
        const errorMessage = error.response?.data?.message || '삭제에 실패했습니다.';
        setAlertMessage(errorMessage);
        setShowAlertModal(true);
      }
    });
    setShowConfirmModal(true);
  };

  // 페이지 변경 핸들러
  const handlePageChange = (page) => {
    fetchCameraAngles(page);
  };

  // 파일 불러오기 핸들러 (기존 방식)
  const handleFileImport = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv,.xlsx,.xls';
    
    fileInput.onchange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setModalInputs(prev => ({
          ...prev,
          angleDataFile: file,
          cameraData: file.name
        }));
        
        setAlertMessage(`파일 "${file.name}"이(가) 선택되었습니다.`);
        setShowAlertModal(true);
      }
    };
    
    fileInput.click();
  };


  // 날짜 포맷팅 헬퍼
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '무제한';
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}${month}${day}`;
  };

  // 지점 선택 핸들러
  const handleBranchSelect = (type, id) => {
    if (type === 'available') {
      setSelectedAvailableBranch(id);
      setSelectedAddedBranch(null);
    } else {
      setSelectedAddedBranch(id);
      setSelectedAvailableBranch(null);
    }
  };
  
  // 지점 추가 핸들러
  const handleAddBranch = (branchesToAdd) => {
    if (!branchesToAdd || branchesToAdd.length === 0) return;
    
    if (typeof branchesToAdd === 'string') {
      const branchToAdd = availableBranches.find(b => b.id === branchesToAdd);
      if (!branchToAdd) return;
      
      const isDuplicate = selectedBranches.some(b => b.id === branchToAdd.id);
      if (isDuplicate) return;
      
      setSelectedBranches([...selectedBranches, branchToAdd]);
      setSelectedAvailableBranch(null);
      return;
    }
    
    const newBranches = branchesToAdd.filter(branch => 
      !selectedBranches.some(b => b.id === branch.id)
    );
    
    if (newBranches.length > 0) {
      setSelectedBranches([...selectedBranches, ...newBranches]);
    }
  };
  
  // 지점 제거 핸들러
  const handleRemoveBranch = (branchIds) => {
    if (!branchIds || branchIds.length === 0) return;
    
    if (typeof branchIds === 'string') {
      if (branchIds === 'branch-default') return;
      
      setSelectedBranches(selectedBranches.filter(b => b.id !== branchIds));
      setSelectedAddedBranch(null);
      return;
    }
    
    setSelectedBranches(selectedBranches.filter(b => !branchIds.includes(b.id)));
  };
  
  // 상세정보 아이콘 클릭 핸들러
  const handleInfoClick = (angle) => {
    setCurrentAngle(angle);
    setShowDetailModal(true);
  };

  // 앵글 타입 표시 헬퍼
  const getAngleTypeDisplay = (angle) => {
    if (angle.isGreeting) {
      return 'greeting';
    } else if (angle.isPhoto && angle.isVideo) {
      return '동영상 + 사진';
    } else if (angle.isPhoto) {
      return '사진';
    } else if (angle.isVideo) {
      return '동영상';
    } else {
      return '미설정';
    }
  };

  // boolean 값에서 앵글 타입 코드 생성
  const getAngleTypeCodeFromBoolean = (isPhoto, isVideo, isGreeting) => {
    if (isGreeting) {
      return 'GREETING';
    } else if (isPhoto && isVideo) {
      return 'PHOTO_VIDEO';
    } else if (isPhoto) {
      return 'PHOTO';
    } else if (isVideo) {
      return 'VIDEO';
    } else {
      return 'PHOTO'; // 기본값
    }
  };

  // 앵글 타입 코드에서 boolean 값 생성
  const getBooleanFromAngleTypeCode = (angleTypeCode) => {
    switch (angleTypeCode) {
      case 'PHOTO':
        return { isPhoto: true, isVideo: false, isGreeting: false };
      case 'VIDEO':
        return { isPhoto: false, isVideo: true, isGreeting: false };
      case 'PHOTO_VIDEO':
        return { isPhoto: true, isVideo: true, isGreeting: false };
      case 'GREETING':
        return { isPhoto: false, isVideo: false, isGreeting: true };
      default:
        return { isPhoto: true, isVideo: false, isGreeting: false };
    }
  };

  return (
    <>
      <div className="page-title-container">
        <h1 className="page-title">카메라 앵글</h1>
        <div className="page-title-icon">
          <a href="#" id="add-angle-btn" title="카메라 앵글 추가" onClick={handleAddAngleClick}>
            <i className="ri-add-circle-line"></i>
          </a>
        </div>
      </div>
      
      <div className="page-container">
        
        {/* 필터 드롭다운 */}
        <div className="filter-dropdown-container-row">
          {/* 일괄 삭제 버튼 */}
          <div className={`bulk-delete-container ${selectedItems.length >= 2 ? 'active' : ''}`}>
            <button className="bulk-delete-btn" onClick={handleBulkDelete}>
              일괄 삭제 ({selectedItems.length})
            </button>
          </div>

          <div className="filter-dropdown">
            <select 
              value={searchFilters.sortBy} 
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
            >
              <option value="createdAt">최신순</option>
              <option value="angleName">이름순</option>
              <option value="startDatetime">사용기간순</option>
            </select>
          </div>
        </div>
        
        {/* 카메라 앵글 테이블 */}
        <div className="common-table-container">
          {loading ? (
            <div className="loading-container">
              <p>로딩 중...</p>
            </div>
          ) : (
            <table className="common-table">
              <thead>
                <tr>
                  <th>
                    <div className="checkbox-container">
                      <input 
                        type="checkbox" 
                        id="select-all" 
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                      <label htmlFor="select-all" className="custom-checkbox"></label>
                    </div>
                  </th>
                  <th>NO</th>
                  <th>앵글명</th>
                  <th>타입</th>
                  <th>사용 기간</th>
                  <th>배포 여부</th>
                  <th>상세정보</th>
                  <th>수정</th>
                  <th>삭제</th>
                </tr>
              </thead>
              <tbody>
                {cameraAngles.map((angle, index) => (
                  <tr key={angle.id}>
                    <td>
                      <div className="checkbox-container">
                        <input 
                          type="checkbox" 
                          id={`angle-${angle.id}`}
                          checked={selectedItems.includes(angle.id)}
                          onChange={(e) => handleItemSelect(angle.id, e)}
                        />
                        <label htmlFor={`angle-${angle.id}`} className="custom-checkbox"></label>
                      </div>
                    </td>
                    <td>{pagination.currentPage * pagination.size + index + 1}</td>
                    <td>{angle.angleName}</td>
                    <td>{getAngleTypeDisplay(angle)}</td>
                    <td className="usage-period">
                      {formatDateForDisplay(angle.startDatetime)} - {formatDateForDisplay(angle.endDatetime)}
                    </td>
                    <td>{angle.isDeployed ? 'Y' : 'N'}</td>
                    <td>
                      <button className="info-btn" onClick={() => handleInfoClick(angle)}>
                        <i className="ri-information-line"></i>
                      </button>
                    </td>
                    <td>
                      <button className="edit-btn" onClick={() => handleEditClick(angle)}>
                        <i className="ri-pencil-line"></i>
                      </button>
                    </td>
                    <td>
                      <button className="delete-btn" onClick={() => handleDeleteClick(angle)}>
                        <i className="ri-close-line"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        
        {/* 페이지네이션 */}
        <PaginationZeroBased
          page={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          size={pagination.size}
          hasNext={pagination.hasNext}
          hasPrevious={pagination.hasPrevious}
          onPageChange={handlePageChange}
        />
      </div>
      
      {/* 카메라 앵글 추가 모달 */}
      <CameraAngleInputModal
        title="카메라 앵글 추가"
        isVisible={showAddModal}
        inputs={modalInputs}
        setInputs={setModalInputs}
        onClose={() => setShowAddModal(false)}
        onConfirm={handleConfirm}
        onCheckDuplicate={handleCheckDuplicate}
        availableBranches={availableBranches}
        selectedBranches={selectedBranches}
        onAddBranch={handleAddBranch}
        onRemoveBranch={handleRemoveBranch}
        onBranchSelect={handleBranchSelect}
        selectedAvailableBranch={selectedAvailableBranch}
        selectedAddedBranch={selectedAddedBranch}
        isEdit={false}
        onFileImport={handleFileImport}
      />
      
      {/* 카메라 앵글 수정 모달 */}
      <CameraAngleInputModal
        title="카메라 앵글 수정"
        isVisible={showEditModal}
        inputs={modalInputs}
        setInputs={setModalInputs}
        onClose={() => setShowEditModal(false)}
        onConfirm={handleConfirm}
        onCheckDuplicate={handleCheckDuplicate}
        availableBranches={availableBranches}
        selectedBranches={selectedBranches}
        onAddBranch={handleAddBranch}
        onRemoveBranch={handleRemoveBranch}
        onBranchSelect={handleBranchSelect}
        selectedAvailableBranch={selectedAvailableBranch}
        selectedAddedBranch={selectedAddedBranch}
        isEdit={true}
        onFileImport={handleFileImport}
      />
      
      {/* 상세정보 모달 */}
      <CameraAngleDetailModal
        isVisible={showDetailModal}
        cameraAngle={currentAngle}
        onClose={() => setShowDetailModal(false)}
      />
      
      {/* 알림 모달 */}
      <CameraAngleConfirmModal
        isVisible={showAlertModal}
        title="알림"
        message={alertMessage}
        type="alert"
        onConfirm={() => setShowAlertModal(false)}
        onCancel={() => setShowAlertModal(false)}
      />
      
      {/* 확인 모달 */}
      <CameraAngleConfirmModal
        isVisible={showConfirmModal}
        title="확인"
        message={confirmMessage}
        type="confirm"
        onConfirm={() => {
          if (confirmCallback) {
            confirmCallback();
          }
          setShowConfirmModal(false);
        }}
        onCancel={() => setShowConfirmModal(false)}
        confirmText="예"
        cancelText="아니오"
      />
    </>
  );
};

export default CameraAngleManagement;
